import { UserInfo } from '@types';
import { config } from '../../../config';
import React, { useMemo, useState } from 'react';
import { selectSelectedLoanNumber, selectUserInfo } from '@store/selectors/widgets.selectors';
import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router-dom';
import { withHeaderFooter } from '../../HOCs/withHeaderFooter/withHeaderFooter';
import { ReFiNavigationProps } from '../../data';
import { I18nextProvider } from 'react-i18next';
import { Container } from '@mui/material';
import { AuthenticatedLeadPage, i18n } from '@fmc-digital/marketing';
import { ErrorMessage, getSteps, SuccessMessage } from '@utils/auth-lead-form';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { instance } from '@utils/fetch';
import { logUserEvent } from '@analytics/amplifyLogging';
import { useSnackBarProps } from '@hooks/useSnackbarProps';
import { removeHVtileChildProduct, removeTileOffer } from '@store/actions/widgets.actions';
import { activityEventUpdate } from 'fmc-digital-common';
import { useWidgetsQueryClient } from '@hooks/useWidgetsQueryClient';
import { useVWOContext } from 'context/VWOContext';
import { allowedVWOActionsMapping } from '@constants';

export const RefiForm = withHeaderFooter(
    () => {
        const { RecordVWOEvent } = useVWOContext();
        const navigate = useNavigate();
        const {
            state: { offerType, leadOriginationSource, loanNumber, portCode, ...rest }
        } = useLocation();
        const userInfo: UserInfo = useSelector(selectUserInfo);
        const selectedLoan = useSelector(selectSelectedLoanNumber);
        const { address1, address2, city, state, zip } = selectedLoan.propertyAddress;
        const { email, firstName, lastName, mobile } = userInfo;
        const isPersonalizedCashOut = isEmpty(rest);
        const steps = getSteps('SL');
        const [applicationId, setApplicationId] = useState('');
        const { enqueueSnackbar } = useSnackbar();
        const successSnackBar = useSnackBarProps({ severity: 'success' });
        const errorSnackBar = useSnackBarProps({ severity: 'error' });

        const dispatch = useDispatch();
        const { updateCache } = useWidgetsQueryClient();

        const name = useMemo(() => {
            return rest?.product_Campaign.toUpperCase() === 'SL' ? 'StreamLine' : 'RateTerm';
        }, [rest?.product_Campaign]);
        const mutation = useMutation({
            mutationFn: ({ values, type }: any) => {
                return instance.request({
                    method: type === 'update' && applicationId ? 'put' : 'post',
                    url: applicationId ? `${config.env('leadSubmissionUrl')}/${applicationId}` : config.env('leadSubmissionUrl'),
                    data: values
                });
            },
            onSuccess: ({ data }, variables) => {
                setApplicationId(data.id);
                if (variables.type === 'submit') {
                    logUserEvent({
                        productCode: offerType || 'NA',
                        loanType: portCode || 'NA',
                        type: `${name}AuthenticatedLeadForm`,
                        action: `${name}LeadSubmitSuccess`
                    });
                    const url = config.env('activityUpdateUrl');
                    activityEventUpdate(
                        [
                            {
                                loanNumber: selectedLoan.identifier,
                                activityType: offerType,
                                activityResult: 'SUBMITTED'
                            }
                        ],
                        url
                    );
                    const offerId = { loanNumber: selectedLoan.identifier, product: offerType, offerGUID: rest?.offerGUID };
                    updateCache(offerId);
                    if (offerType.indexOf('HV') === 0) dispatch(removeHVtileChildProduct(offerId));
                    else dispatch(removeTileOffer(offerId));

                    enqueueSnackbar(SuccessMessage('AuthRefi'), successSnackBar);
                    navigate('/');
                    const vwoEvent = allowedVWOActionsMapping[`${name}LeadSubmitSuccess` || null];
                    vwoEvent ? RecordVWOEvent(vwoEvent, { product: offerType }) : null;
                    return;
                }
                logUserEvent({
                    productCode: offerType || 'NA',
                    loanType: portCode || 'NA',
                    type: `${name}AuthenticatedLeadForm`,
                    action: `${name}LeadUpdateSuccess`
                });
                const vwoEvent = allowedVWOActionsMapping[`${name}LeadUpdateSuccess` || null];
                vwoEvent ? RecordVWOEvent(vwoEvent, { product: offerType }) : null;
            },
            onError: (_, variables) => {
                if (variables.type === 'submit') {
                    logUserEvent({
                        productCode: offerType || 'NA',
                        loanType: portCode || 'NA',
                        type: `${name}AuthenticatedLeadForm`,
                        action: `${name}LeadSubmitFailed`
                    });
                    enqueueSnackbar(ErrorMessage('AuthRefi'), errorSnackBar);
                    return;
                }
                logUserEvent({
                    productCode: offerType || 'NA',
                    loanType: portCode || 'NA',
                    type: `${name}AuthenticatedLeadForm`,
                    action: `${name}LeadUpdateFailed`
                });
            }
        });
        const defaultValues = React.useMemo(() => {
            return {
                offerGUID: rest?.offerGUID,
                leadOriginationSource: leadOriginationSource,
                product: offerType,
                borrowers: [
                    {
                        address: {
                            street: address1,
                            street2: address2,
                            city: city,
                            state: state,
                            zipCode: zip
                        },
                        firstName: firstName,
                        middleName: '',
                        lastName: lastName,
                        nameSuffix: '',
                        email: email,
                        phone: mobile
                    }
                ],
                loanNumber: selectedLoan.identifier,
                productInfo: {
                    type: 'refinance',
                    refinanceDetails: {
                        type: rest?.product_Campaign?.toUpperCase()
                    }
                },
                referralSource: 'ACP',
                utmDetails: {
                    utmDevice: 'Web',
                    utmDevicemodel: 'Chrome'
                }
            };
        }, [isPersonalizedCashOut, rest, selectedLoan]);

        React.useEffect(() => {
            logUserEvent({
                productCode: offerType || 'NA',
                loanType: portCode || 'NA',
                type: `${name}AuthenticatedLeadForm`,
                action: `Submitting${name}InitialLead`
            });
            const vwoEvent = allowedVWOActionsMapping[`Submitting${name}InitialLead` || null];
            vwoEvent ? RecordVWOEvent(vwoEvent, { product: offerType }) : null;
            mutation.mutate({ type: 'init', values: defaultValues });
        }, [name]);

        if (rest?.product_Campaign.toUpperCase() === 'SL') {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            defaultValues.productInfo.refinanceDetails.streamLineDetails = {};
        }

        if (rest?.product_Campaign.toUpperCase() === 'RT') {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            defaultValues.productInfo.refinanceDetails.rateTermDetails = {};
        }
        return (
            <I18nextProvider i18n={i18n}>
                <Container maxWidth="md">
                    <AuthenticatedLeadPage
                        returnToDashboard={() => navigate('../')}
                        tollFreeNo="8334481927"
                        steps={steps}
                        defaultValues={defaultValues}
                        handleSubmitOrUpdate={(type, values) => {
                            if (!mutation.isLoading) mutation.mutate({ type, values });
                        }}
                    />
                </Container>
            </I18nextProvider>
        );
    },
    { ...ReFiNavigationProps }
);
